import { useUser } from "contexts/UserContext";
import moment, { MomentInput } from "moment-timezone";
import { useMemo } from "react";
import { handleServerTimeDifference } from "services/adminAutoNotifications/helpers";
import { getSessionUser } from "services/users/auth";
import { User } from "services/users/model";
import { LocalStorageKeys } from "./storage";

export const DateFormat = {
  compactDate: "MMM D, YYYY", // Mar 5, 2026
  fullDate: "MMMM D, YYYY", // March 5, 2026
  monthDay: "MMM D", // Mar 5
  weekdayDate: "ddd, MMM D", // Thu, Mar 5
  weekday: "dddd", // Thursday
  weekdayTime: "dddd · HH:mm", // Thursday · 14:30

  sessionDateTime: "dddd, MMM D, HH:mm", // Thursday, Mar 5, 14:30
  compactDateTime: "MMM D, YYYY, HH:mm", // Mar 5, 2026, 14:30
  compactDateTimeWithOffset: "MMM D, YYYY, HH:mm Z", // Mar 5, 2026, 14:30 +01:00

  fullTime: "HH:mm", // 14:30
} as const;

export function useUserTimezone() {
  const timezoneId = useUser().profile.timezone.id;

  return useMemo(
    () => ({
      timezoneId,
      moment: timezoneMoment(timezoneId),
      format: (date: MomentInput, formatStr: keyof typeof DateFormat) =>
        moment.tz(date, timezoneId).format(DateFormat[formatStr]),
    }),
    [timezoneId]
  );
}

export const timezoneMoment =
  (tz: string) =>
  (time?: MomentInput): moment.Moment =>
    time === undefined ? moment.tz(tz) : moment.tz(time, tz);

export function localMoment(input?: MomentInput): moment.Moment {
  const zone = moment.tz.guess();
  return input === undefined ? moment.tz(zone) : moment.tz(input, zone);
}

export function formatInUserTimezone(
  date: MomentInput,
  formatStr: keyof typeof DateFormat
) {
  return moment.tz(date, getUserTimezoneId()).format(DateFormat[formatStr]);
}

export function getUserTimezoneId() {
  const user = getSessionUser(true);
  return user.profile.timezone?.id ?? moment.tz.guess();
}

export function getLocalTimezoneDisplay() {
  return getTimezoneDisplay(moment.tz.guess());
}

export function getUserTimezoneDisplay() {
  return getTimezoneDisplay(getUserTimezoneId());
}

export function getUTCOffset(timezoneId: string) {
  return moment.tz(timezoneId).format("Z");
}

export function getTimezoneDisplay(timezoneId: string) {
  const utcOffset = moment.tz(timezoneId).format("Z");
  return `${utcOffset} ${timezoneId}`.replace(TIMEZONE_REGION_PREFIX, " ");
}

const TIMEZONE_REGION_PREFIX = /( .+\/)/gi;

export function getTimezones(): string[] {
  return moment.tz
    .names()
    .filter(timezoneId => timezoneId.includes("/") && !timezoneId.startsWith("Etc/"))
    .map(getTimezoneDisplay)
    .sort(compareTimezones);
}

export function getTimezoneIdByPartialName() {
  const name = getLocalTimezoneDisplay().split(" ")[1] ?? "";
  return moment.tz.names().find(tzName => tzName.includes(name));
}

function compareTimezones(a: string, b: string) {
  const offsetDifference = getTimezoneOffsetInMinutes(a) - getTimezoneOffsetInMinutes(b);
  if (offsetDifference !== 0) {
    return offsetDifference;
  }

  return a.slice(7).localeCompare(b.slice(7));

  function getTimezoneOffsetInMinutes(timezone: string) {
    const sign = timezone[0] === "-" ? -1 : 1;
    const hours = Number.parseInt(timezone.slice(1, 3), 10);
    const minutes = Number.parseInt(timezone.slice(4, 6), 10);
    return sign * (hours * 60 + minutes);
  }
}

export function compareWithLocalTimezone(timezoneId: string) {
  const localTimezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const areTimezonesEquivalent =
    moment.tz.zone(localTimezoneId)?.name === moment.tz.zone(timezoneId)?.name;
  return areTimezonesEquivalent;
}

const SERVER_TIME_DIFFERENCE = 5 * 60 * 1000; // 5 minutes.

export function saveServerTimeOffset(serverDateHeader: string) {
  const serverTimeOffset = new Date(serverDateHeader).getTime() - new Date().getTime();
  localStorage.setItem(LocalStorageKeys.serverTimeOffset, serverTimeOffset.toString());
}

export function getCurrentServerTime(): moment.Moment {
  const offset = getServerTimeOffset();
  return offset === null ? moment.utc() : moment.utc().add(offset, "milliseconds");
}

export function getServerTimeOffset() {
  const storedOffset = localStorage.getItem(LocalStorageKeys.serverTimeOffset);
  if (!storedOffset) {
    return null;
  }

  const offset = Number(storedOffset);
  // Ignore invalid persisted values like NaN/Infinity and force callers to handle "no offset".
  return Number.isFinite(offset) ? offset : null;
}

export const getTimeStringFromMinutes = (minutes: number | null | undefined) => {
  if (minutes) {
    const hour = Math.floor((minutes || 0) / 60);
    const min = minutes - hour * 60;
    if (hour) {
      return `${hour} hour${hour === 1 ? "" : "s"}${min > 0 ? ` ${min} min${min === 1 ? "" : "s"}` : ""}`;
    } else if (min) {
      return `${min} min${min === 1 ? "" : "s"}`;
    } else {
      return "0";
    }
  }

  return "0";
};

// Buckets a past instant into the largest unit where the diff fits, for i18n relative-time rendering.
// Returns `{ range: "minute" | "hour" | "day" | "month" | "year", time }`,
// where `time` is the signed value to feed into the locale string (positive = in the future).
export const getRelativeTimeLocaleOption = (time: MomentInput) => {
  const timeObj = moment.utc(time);
  // For each unit, the threshold = how many of that unit before we promote to the next coarser one.
  const unitThresholds = {
    minute: 60,
    hour: 12,
    day: 30,
    month: 12,
  } satisfies Record<string, number>;

  const range =
    (Object.keys(unitThresholds) as Array<keyof typeof unitThresholds>).find(
      unit => moment.utc().diff(timeObj, `${unit}s`) < unitThresholds[unit]
    ) ?? "year";

  return { range, time: -moment.utc().diff(timeObj, `${range}s`) };
};

export function checkServerTimeDifference(user: User) {
  // Checks if time difference between server time and user's local time is more than ${SERVER_TIME_DIFFERENCE}.
  if (!user.student && !user.instructor) {
    return;
  }

  const offset = getServerTimeOffset();
  if (offset !== null && Math.abs(offset) > SERVER_TIME_DIFFERENCE) {
    handleServerTimeDifference({
      user,
      offset,
    });
  }
}
